import { useCallback, useEffect, useState } from "react";
import { fetchPortfolioHistory } from "../services/api.js";

/**
 * Portfolio-value-over-time series for the Dashboard's Total Returns
 * chart. Backend (transaction.controller.js → portfolioHistory) replays
 * the wallet's deposit lifecycle and returns the samples — frontend
 * just renders them, so multiple dashboard surfaces converge on the
 * same numbers.
 */
export function usePortfolioHistory(owner, { windowId = "7d", samples = 61, pollMs = 30_000 } = {}) {
    const [data, setData] = useState(null);
    const [isLoading, setLoading] = useState(Boolean(owner));
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        if (!owner) {
            setData(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const resp = await fetchPortfolioHistory(owner, { windowId, samples });
            setData({
                samples: (resp.samples || []).map((s) => ({
                    timestamp: Number(s.timestamp),
                    value: BigInt(s.value || "0")
                })),
                peak: resp.peak
                    ? { timestamp: Number(resp.peak.timestamp), value: BigInt(resp.peak.value || "0") }
                    : null,
                windowId: resp.windowId,
                fromTime: resp.fromTime,
                toTime: resp.toTime
            });
        } catch (err) {
            setError(err?.message || "Failed to fetch portfolio history");
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [owner, windowId, samples]);

    useEffect(() => {
        refresh();
        if (!pollMs) return undefined;
        const handle = setInterval(refresh, pollMs);
        return () => clearInterval(handle);
    }, [refresh, pollMs]);

    return { data, isLoading, error, refresh };
}
